import { createContext, useContext } from "react";
/**
 * Context that provides access to the Dialog's DOM element.
 *
 * This is used by child components that need to render portals or poppers
 * within the dialog's stacking context. Without this, elements like tooltips
 * would render outside the dialog and have z-index/positioning issues.
 *
 * The value is the element itself (set via useState once the dialog mounts),
 * not a ref — consumers like Radix's Portal read the container during render,
 * so the context value's identity must change to trigger a re-render once the
 * dialog node exists. A ref's `.current` mutating does not do that.
 *
 * @example
 * // Consuming the context (automatically done by Tooltip component)
 * const container = useDialogContainer();
 * <Portal container={container} />
 */
export var DialogContainerContext = createContext(null);
/**
 * Hook to access the nearest Dialog's container element.
 * Returns null when not inside a Dialog, or before the dialog node has mounted.
 *
 * Note: The Tooltip component automatically uses this hook to render
 * its Portal within the dialog's stacking context.
 */
export var useDialogContainer = function () { return useContext(DialogContainerContext); };
